import type { ChatParticipant } from '../common'
import type { ReactNode } from 'react'

import cx from 'classnames'
import { format } from 'date-fns'
import { LuCheck, LuCheckCheck } from 'react-icons/lu'
import { MdMoreVert } from 'react-icons/md'

import { formatLastMessageDateTime, tagClassNameMapper, tagNameMapper } from '../common'

import { NameTag } from './name-tag'
import { ParticipantsAvatars } from './participants-avatars'

interface Message {
  id: string
  author: ChatParticipant
  content: ReactNode
  sentAt: Date
}

export interface ChatListItemProps extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onContextMenu'> {
  isActive: boolean
  isConcierge: boolean
  patient: ChatParticipant
  participants: Array<ChatParticipant & { readAt?: Date }>
  excludedParticipantAvatarIds?: string[]
  lastMessage?: Message
  hasUnreadMessages: boolean
  isMarkedUnread: boolean
  isSelected?: boolean
  tags?: string[]
  readAt?: Date
  patientReadAt?: Date
  readTicks?: number
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => void
}

function formatDateTime (date: Date): string {
  return format(date, 'MM/dd/yyyy HH:mm')
}

export function ChatListItem ({
  isActive,
  isConcierge,
  patient,
  participants,
  excludedParticipantAvatarIds,
  lastMessage,
  readAt,
  patientReadAt,
  readTicks,
  hasUnreadMessages,
  isSelected = false,
  isMarkedUnread,
  tags,
  ...rest
}: ChatListItemProps): JSX.Element {
  let patientIsPresent = participants.some(p => p.id === patient.id)

  if (
    readAt &&
    !isNaN(readAt.getTime()) &&
    typeof readTicks !== 'number'
  ) {
    readTicks = 2
  }

  let ReadIconComponent = readTicks === 1
    ? LuCheck
    : readTicks === 2
      ? LuCheckCheck
      : undefined

  return (
    <button
      type='button'
      className={cx(
        'flex flex-row w-full pb-3 pt-1.5 px-4 border-b border-b-gray-200',
        isSelected && 'bg-sky-50'
      )}
      {...rest}
    >
      <div className='flex flex-col w-full flex-grow'>
        <div className='flex flex-row items-center justify-between pt-1'>
          <div
            className={cx(
              'flex flex-row w-full items-center justify-start',
              patientIsPresent && 'gap-2',
              !patientIsPresent && 'gap-4'
            )}
          >
            <NameTag isAbsent={!patientIsPresent}>
              {patient.name}
            </NameTag>

            {(hasUnreadMessages || isMarkedUnread) && (
              <div
                className={cx(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  hasUnreadMessages && 'bg-blue-500',
                  isMarkedUnread && 'bg-purple-500'
                )}
              />
            )}
          </div>

          <div className='flex flex-row items-center gap-1'>
            <ParticipantsAvatars
              participants={
                participants
                  .filter((a) => a.visible)
                  .sort((a, b) =>
                    (a?.type === 'patient' ? 0 : 1) - (b?.type === 'patient' ? 0 : 1))

              }
              excludedIds={excludedParticipantAvatarIds}
            />

            <div
              role='button'
              tabIndex={0}
              className='cursor-pointer'
              onKeyDown={() => {
                // TODO: show menu
              }}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()

                let rect = event.currentTarget.getBoundingClientRect()

                event.clientX = rect.left + rect.width / 2
                event.clientY = rect.top + rect.height / 2

                rest.onContextMenu?.(event)
              }}
              title={typeof rest.onContextMenu === 'function' ? 'More options' : undefined}
            >
              <MdMoreVert
                size={18}
                className='text-gray-600 -mr-2'
              />
            </div>
          </div>
        </div>

        {isConcierge && Array.isArray(tags) && tags.length > 0 && (
          <div className='flex flex-row overflow-x-scroll no-scrollbar pt-2 pb-0 gap-2 -mx-1'>
            {tags.map(tag => (
              <div
                key={tag}
                className={cx(
                  'whitespace-nowrap p-1 px-1.5 text-xs font-medium font-roboto',
                  tagClassNameMapper[tag] ?? 'text-slate-600 bg-gray-100'
                )}
              >
                {tagNameMapper[tag] ?? tag}
              </div>
            ))}
          </div>
        )}

        <div className='flex flex-row items-center justify-start pt-1'>
          {lastMessage ? lastMessage.content : 'No messages yet'}
        </div>
        <div className='flex flex-row items-center justify-between'>
          <span className='flex-grow font-roboto font-normal text-sm text-gray-500 line-clamp-1'>
            {lastMessage?.author.name}
            {!lastMessage && (<>&nbsp;</>)}
          </span>

          <div className='flex gap-2 items-center'>
            {readAt && ReadIconComponent && (
              <div
                className='flex justify-center items-center text-blue-600 p-1 -m-1'
                title={tooltip({ participants, excludedIds: [ lastMessage?.author.id ?? '' ] })}
              >
                <ReadIconComponent
                  size={14}
                  className='pointer-events-none'
                />
              </div>
            )}

            {lastMessage && (
              <span
                className='font-roboto text-xs text-gray-500 block p-1 -m-1'
                title={`Last message sent at ${formatDateTime(lastMessage.sentAt)}\n${tooltip({ participants, excludedIds: [ lastMessage.author.id ] })}`}
              >
                {formatLastMessageDateTime(lastMessage.sentAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

interface TooltipProps {
  participants: Array<ChatParticipant & {readAt?: Date}>
  excludedIds?: string[]
}

function tooltip ({ participants, excludedIds = [] }: TooltipProps): string {
  return participants
    .filter(p => !excludedIds.includes(p.id))
    .sort((a, b) => {
      if (a.type === 'patient') {
        return -1
      }

      if (b.type === 'patient') {
        return 1
      }

      let ar = a.readAt
      let br = b.readAt

      if (ar && !br) {
        return -1
      }

      if (!ar && br) {
        return 1
      }

      if (!ar && !br) {
        return 0
      }

      if (ar && br) {
        return br.getTime() - ar.getTime()
      }

      return 0
    })
    .map(p => {
      let result = p.name

      if (p.type === 'patient') {
        result += ' (patient)'
      }

      if (p.readAt) {
        result += ` read at ${formatDateTime(p.readAt)}`
      }

      if (!p.readAt) {
        result += ' hasn\'t read yet'
      }

      return result
    })
    .join('\n')
}
